// AlarmSettings - Admin Config tab for the drink / dessert / chips order alarms.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { listVoices, pickVoice, speak, VOICE_KEY } from '../alerts/OrderAlarm';

const api = axios.create({ baseURL: API_URL, withCredentials: true });
const LABEL = { drinks: '🥤 Drinks', desserts: '🍰 Desserts', chips: '🌮 Chips', takeout: '🥢 Take-out items (utensils, napkins) when YES' };

export default function AlarmSettings() {
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [screens, setScreens] = useState([]);
  const [status, setStatus] = useState('');
  const [tryName, setTryName] = useState('');
  const [tryResult, setTryResult] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voiceName, setVoiceName] = useState(() => { try { return localStorage.getItem(VOICE_KEY) || ''; } catch { return ''; } });

  useEffect(() => {
    const refresh = () => setVoices(listVoices());
    refresh();
    if ('speechSynthesis' in window) window.speechSynthesis.addEventListener('voiceschanged', refresh);
    return () => { if ('speechSynthesis' in window) window.speechSynthesis.removeEventListener('voiceschanged', refresh); };
  }, []);

  const chooseVoice = (name) => {
    setVoiceName(name);
    try { if (name) localStorage.setItem(VOICE_KEY, name); else localStorage.removeItem(VOICE_KEY); } catch {}
  };
  const sampleVoice = () => speak("Heads up! GrubHub delivery order for Maria has 2 Mexican Coke and 1 Tres Leches Cake. Don't forget the drinks and dessert!");

  const load = async () => {
    try {
      const [s, r, a] = await Promise.all([
        api.get('/alerts/settings'), api.get('/alerts/rules'), api.get('/alerts/recent', { params: { limit: 20 } })
      ]);
      setSettings(s.data.settings);
      setRules(r.data.map(x => ({ ...x, keywords_text: (x.keywords || []).join(', '), exclude_text: (x.exclude_keywords || []).join(', ') })));
      setAlerts(a.data.alerts || []);
      setScreens(a.data.screens || []);
    } catch (e) {
      setStatus(`Load failed: ${e.response?.data?.error || e.message}`);
    }
  };
  useEffect(() => { load(); }, []);

  const flash = (msg) => { setStatus(msg); setTimeout(() => setStatus(''), 4000); };

  const saveSettings = async () => {
    try {
      const r = await api.put('/alerts/settings', settings);
      setSettings(r.data.settings);
      flash('Settings saved');
    } catch (e) { flash(`Save failed: ${e.response?.data?.error || e.message}`); }
  };

  const saveRule = async (rule) => {
    try {
      await api.put(`/alerts/rules/${rule.id}`, { keywords: rule.keywords_text, exclude_keywords: rule.exclude_text, is_active: rule.is_active });
      flash(`${LABEL[rule.category]} rule saved`);
      load();
    } catch (e) { flash(`Save failed: ${e.response?.data?.error || e.message}`); }
  };

  const fireTest = async () => {
    try {
      const r = await api.post('/alerts/test', {});
      flash(`Test alarm sent to ${r.data.screens.length} screen(s): ${r.data.summary}`);
      setTimeout(load, 5000);
    } catch (e) { flash(`Test failed: ${e.response?.data?.error || e.message}`); }
  };

  const tryClassify = async () => {
    try {
      const r = await api.get('/alerts/classify', { params: { name: tryName } });
      setTryResult(r.data);
    } catch (e) { setTryResult({ error: e.message }); }
  };

  const listField = (key) => ({
    value: (settings[key] || []).join(', '),
    onChange: (e) => setSettings({ ...settings, [key]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }),
  });

  if (!settings) return <div className="text-gray-400">Loading alarm settings… {status}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Order Alarms (drinks · desserts · chips)</h2>
        <button onClick={fireTest} className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded font-bold">🚨 Fire a test alarm on every screen</button>
      </div>
      {status && <div className="bg-blue-900/50 text-blue-100 px-3 py-2 rounded mb-4">{status}</div>}

      <div className="bg-gray-800 p-4 rounded mb-6">
        <h3 className="font-semibold mb-3">Screens connected right now</h3>
        {screens.length === 0 ? <div className="text-red-300">No Barrio Bucks screen is reporting in. Nobody would hear an alarm.</div> : (
          <ul className="text-sm space-y-1">
            {screens.map((s, i) => (
              <li key={i} className={!s.atShop ? 'text-gray-400' : s.soundEnabled && s.audioUnlocked ? 'text-green-300' : 'text-red-300'}>
                {s.soundEnabled && s.audioUnlocked ? '🔊' : '🔇'} {s.name} — {s.atShop ? '🏠 at the shop' : `🚫 off-site (${s.ip}) — does not count`} — {s.page || '/'} — {s.visible ? 'visible' : 'in background'}
                {s.soundEnabled && !s.audioUnlocked ? ' — sound on but the browser needs one click first' : ''}
                {!s.soundEnabled ? ' — sound turned off on that screen' : ''}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-gray-800 p-4 rounded mb-6">
        <h3 className="font-semibold mb-1">Voice on this screen</h3>
        <p className="text-xs text-gray-400 mb-2">Voices come from this computer's browser, so pick one on each screen that plays alarms. The current pick is <span className="text-white">{(pickVoice() || {}).name || 'browser default'}</span>.</p>
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <select className="bg-gray-700 text-white px-2 py-1 rounded" value={voiceName} onChange={e => chooseVoice(e.target.value)}>
            <option value="">Automatic (best available)</option>
            {voices.map(v => <option key={v.name} value={v.name}>{v.name} ({v.lang})</option>)}
          </select>
          <button onClick={sampleVoice} className="bg-gray-600 hover:bg-gray-500 px-3 py-1 rounded">▶ Hear a sample</button>
          {voices.length === 0 && <span className="text-gray-400">No voices reported yet — click anywhere on the page, then reopen this tab.</span>}
        </div>
      </div>

      <div className="bg-gray-800 p-4 rounded mb-6">
        <h3 className="font-semibold mb-3">Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!settings.enabled} onChange={e => setSettings({ ...settings, enabled: e.target.checked })} /> Alarms enabled</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={settings.repeat_voice !== false} onChange={e => setSettings({ ...settings, repeat_voice: e.target.checked })} /> Read the items out loud twice</label>
          <label className="block">Alarm categories<br />
            <span className="flex flex-wrap gap-4 mt-1">{['drinks', 'desserts', 'chips', 'takeout'].map(c => (
              <label key={c} className="flex items-center gap-1"><input type="checkbox" checked={(settings.categories || []).includes(c)} onChange={e => setSettings({ ...settings, categories: e.target.checked ? [...new Set([...(settings.categories || []), c])] : (settings.categories || []).filter(x => x !== c) })} />{LABEL[c]}</label>
            ))}</span>
          </label>
          <label className="block">Banner stays on screen (seconds)<input type="number" className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" value={settings.overlay_seconds} onChange={e => setSettings({ ...settings, overlay_seconds: Number(e.target.value) })} /></label>
          <label className="block">Skip alarms for these sources (comma-separated)<input className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" {...listField('exclude_sources')} /></label>
          <label className="block">Skip alarms for these order types<input className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" {...listField('exclude_order_types')} /></label>
          <label className="block">Ignore orders older than (minutes) — keeps backfills quiet<input type="number" className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" value={settings.max_order_age_minutes} onChange={e => setSettings({ ...settings, max_order_age_minutes: Number(e.target.value) })} /></label>
          <label className="block">Shop public IP address(es) — only screens on these count as seen/heard<input className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" {...listField('shop_ips')} placeholder="69.119.157.223" /></label>
          <label className="block">Owner phone for texts<input className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" value={settings.owner_phone || ''} onChange={e => setSettings({ ...settings, owner_phone: e.target.value })} placeholder="+1 555 555 5555" /></label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!settings.notify_unheard} onChange={e => setSettings({ ...settings, notify_unheard: e.target.checked })} /> Text the owner when an alarm was not heard on any screen</label>
          <label className="block">…after how many seconds<input type="number" className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" value={settings.unheard_after_seconds} onChange={e => setSettings({ ...settings, unheard_after_seconds: Number(e.target.value) })} /></label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!settings.notify_sound_off} onChange={e => setSettings({ ...settings, notify_sound_off: e.target.checked })} /> Text the owner when no screen has sound on during shop hours</label>
          <label className="block">…at most every (minutes)<input type="number" className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" value={settings.sound_off_every_minutes} onChange={e => setSettings({ ...settings, sound_off_every_minutes: Number(e.target.value) })} /></label>
          <label className="block">Shop hours start (HH:MM)<input className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" value={settings.hours_start} onChange={e => setSettings({ ...settings, hours_start: e.target.value })} /></label>
          <label className="block">Shop hours end (HH:MM)<input className="bg-gray-700 text-white px-2 py-1 rounded w-full mt-1" value={settings.hours_end} onChange={e => setSettings({ ...settings, hours_end: e.target.value })} /></label>
        </div>
        <button onClick={saveSettings} className="mt-4 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded">Save settings</button>
      </div>

      <div className="bg-gray-800 p-4 rounded mb-6">
        <h3 className="font-semibold mb-1">What counts as a drink / dessert / chips</h3>
        <p className="text-xs text-gray-400 mb-3">Items are matched first by their Toast menu group (Drinks, Dessert, Chips 'n' Dips). These keywords catch everything else, like delivery-app names and add-ons. “Exclude” words stop a match, e.g. the “Chips or Soda Combo” burrito.</p>
        {rules.map((rule, i) => (
          <div key={rule.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start mb-3 text-sm">
            <div className="md:col-span-2 font-bold pt-2">{LABEL[rule.category] || rule.category}</div>
            <label className="md:col-span-5 block">Keywords<textarea rows={2} className="bg-gray-700 text-white px-2 py-1 rounded w-full" value={rule.keywords_text} onChange={e => setRules(rules.map((r, j) => j === i ? { ...r, keywords_text: e.target.value } : r))} /></label>
            <label className="md:col-span-3 block">Exclude<textarea rows={2} className="bg-gray-700 text-white px-2 py-1 rounded w-full" value={rule.exclude_text} onChange={e => setRules(rules.map((r, j) => j === i ? { ...r, exclude_text: e.target.value } : r))} /></label>
            <div className="md:col-span-2 flex flex-col gap-1 pt-1">
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.is_active} onChange={e => setRules(rules.map((r, j) => j === i ? { ...r, is_active: e.target.checked } : r))} /> active</label>
              <button onClick={() => saveRule(rule)} className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded">Save</button>
            </div>
          </div>
        ))}
        <div className="mt-4 flex gap-2 items-center text-sm">
          <input className="bg-gray-700 text-white px-2 py-1 rounded flex-1" placeholder="Try an item name, e.g. Mexi Sprite" value={tryName} onChange={e => setTryName(e.target.value)} onKeyDown={e => e.key === 'Enter' && tryClassify()} />
          <button onClick={tryClassify} className="bg-gray-600 hover:bg-gray-500 px-3 py-1 rounded">Check</button>
          {tryResult && (
            <span className={tryResult.category ? 'text-yellow-300' : 'text-gray-400'}>
              {tryResult.error ? tryResult.error : tryResult.category ? `→ ${LABEL[tryResult.category]}` : '→ not flagged'}
              {tryResult.menuMatch ? ` (Toast: ${tryResult.menuMatch.group}, $${tryResult.menuMatch.price})` : ''}
            </span>
          )}
        </div>
      </div>

      <div className="bg-gray-800 p-4 rounded">
        <h3 className="font-semibold mb-3">Last 20 alarms</h3>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-400 border-b border-gray-700"><th className="py-1 pr-2">When</th><th className="pr-2">Order</th><th className="pr-2">Items</th><th className="pr-2">Heard</th><th>Owner texted</th></tr></thead>
          <tbody>
            {alerts.map(a => (
              <tr key={a.id} className="border-b border-gray-700/50">
                <td className="py-1 pr-2 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                <td className="pr-2">{a.is_test ? 'TEST' : `${a.source || ''} ${a.order_type || ''} ${a.customer_name ? '· ' + a.customer_name : ''} #${a.order_id || ''}`}</td>
                <td className="pr-2">{a.summary}</td>
                <td className={`pr-2 ${a.heard_at ? 'text-green-300' : a.seen_at ? 'text-yellow-300' : 'text-red-300'}`}>{a.heard_at ? `🔊 ${a.heard_by}` : a.seen_at ? `👀 ${a.seen_by} (no sound)` : '⚠️ no'}</td>
                <td>{a.owner_notified_at ? new Date(a.owner_notified_at).toLocaleTimeString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
